<template>
  <div>
  <UserManagedHubModal
    :value="value"
    title="我的资源"
    add-tab-label="添加资源"
    manage-tab-label="管理资源"
    :active-tab.sync="innerTab"
    :usage-text="usageText"
    @input="$emit('input', $event)"
    @visible-change="onHubVisibleChange"
  >
    <template #add>
      <AddTtsResourceModal
        embedded
        :resource="null"
        @confirm="handleAddConfirm"
      />
    </template>
    <template #manage="{ switchTab }">
      <div v-if="!resourceList.length" class="vc-my-resources__empty">
        暂无自定义资源，可切换到「添加资源」创建
        <Button type="text" class="vc-my-resources__empty-link" @click="switchTab('add')">
          去添加
        </Button>
      </div>
      <div v-else class="vc-my-resources__list">
        <div
          v-for="row in resourceList"
          :key="row.id"
          class="vc-my-resources__item"
        >
          <div class="vc-my-resources__item-main">
            <FileTextOutlinedIcon :size="18" class="vc-my-resources__item-icon" />
            <span class="vc-my-resources__item-name">{{ row.name || '未命名资源' }}</span>
            <Tag v-if="row.provider" class="vc-my-resources__item-tag">{{ row.provider }}</Tag>
          </div>
          <div class="vc-my-resources__item-actions">
            <EditIconButton size="md" title="编辑" @click="handleEdit(row)" />
            <DeleteIconButton size="md" title="删除" @click="handleDelete(row)" />
          </div>
        </div>
      </div>
    </template>
  </UserManagedHubModal>

  <AddTtsResourceModal
    v-model="editModalVisible"
    :resource="editingResource"
    @confirm="handleEditConfirm"
  />
  </div>
</template>

<script>
import AddTtsResourceModal from './AddTtsResourceModal.vue'
import UserManagedHubModal from './UserManagedHubModal.vue'
import DeleteIconButton from '../shared/DeleteIconButton.vue'
import EditIconButton from '../shared/EditIconButton.vue'
import FileTextOutlinedIcon from '../shared/FileTextOutlinedIcon.vue'
import {
  buildCustomTtsResourceEngineValue,
  createCustomTtsResource,
  isCustomTtsResourceEngine,
  sanitizeCustomTtsResources,
} from '../../utils/tripleAccessHelpers'

export default {
  name: 'MyTtsResourcesModal',
  components: {
    AddTtsResourceModal,
    UserManagedHubModal,
    DeleteIconButton,
    EditIconButton,
    FileTextOutlinedIcon,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    initialTab: {
      type: String,
      default: 'add',
    },
    tripleAccess: {
      type: Object,
      required: true,
    },
    maxMyResources: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      innerTab: 'add',
      editModalVisible: false,
      editingResource: null,
    }
  },
  computed: {
    resourceList() {
      const tts = this.tripleAccess && this.tripleAccess.tts
      return sanitizeCustomTtsResources(tts && tts.custom_resources)
    },
    usageText() {
      return `已用 ${this.resourceList.length}/${this.maxMyResources}`
    },
  },
  methods: {
    onHubVisibleChange(visible) {
      if (visible) {
        this.innerTab = this.initialTab === 'manage' ? 'manage' : 'add'
      }
    },
    ensureCustomResourcesArray() {
      const tts = this.tripleAccess && this.tripleAccess.tts
      if (!tts || typeof tts !== 'object') return
      if (!Array.isArray(tts.custom_resources)) {
        this.$set(tts, 'custom_resources', sanitizeCustomTtsResources(tts.custom_resources))
      }
    },
    handleAddConfirm(payload) {
      this.ensureCustomResourcesArray()
      const tts = this.tripleAccess.tts
      const list = sanitizeCustomTtsResources(tts.custom_resources)
      if (list.length >= this.maxMyResources) {
        this.$Message.warning(`最多保存 ${this.maxMyResources} 个资源`)
        return
      }
      const resource = createCustomTtsResource(payload)
      tts.custom_resources.push(resource)
      tts.engine = buildCustomTtsResourceEngineValue(resource.id)
      this.$Message.success('已添加')
      this.$emit('added', { id: resource.id })
      this.$emit('input', false)
    },
    handleEdit(row) {
      this.editingResource = row ? { ...row } : null
      this.editModalVisible = true
    },
    handleEditConfirm(payload) {
      const id = String(payload && payload.id != null ? payload.id : '').trim()
      if (!id) return

      const tts = this.tripleAccess && this.tripleAccess.tts
      if (!tts || !Array.isArray(tts.custom_resources)) return

      const index = tts.custom_resources.findIndex(
        (item) => String(item && item.id != null ? item.id : '').trim() === id,
      )
      if (index < 0) return

      const updated = {
        ...tts.custom_resources[index],
        name: payload.name,
        provider: payload.provider,
        group_id: payload.group_id,
        key: payload.key,
        description: payload.description,
        type: 'tts',
      }
      this.$set(tts.custom_resources, index, updated)
      this.editingResource = null
      this.editModalVisible = false
      this.$Message.success('已保存')
      this.$emit('updated', { id })
    },
    handleDelete(row) {
      const id = String(row && row.id != null ? row.id : '').trim()
      const name = String(row && row.name != null ? row.name : '').trim() || '该资源'
      if (!id) return

      this.$Modal.confirm({
        title: '删除资源',
        content: `确定删除「${name}」吗？删除后不可恢复。`,
        onOk: () => {
          const tts = this.tripleAccess && this.tripleAccess.tts
          if (!tts || !Array.isArray(tts.custom_resources)) return

          const index = tts.custom_resources.findIndex(
            (item) => String(item && item.id != null ? item.id : '').trim() === id,
          )
          if (index < 0) return

          tts.custom_resources.splice(index, 1)

          const engine = String(tts.engine != null ? tts.engine : '').trim()
          if (isCustomTtsResourceEngine(engine) && engine === buildCustomTtsResourceEngineValue(id)) {
            this.$set(tts, 'engine', '')
            this.$set(tts, 'voice_id', '')
          }

          this.$Message.success('已删除')
          this.$emit('deleted', { id })
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.vc-my-resources {
  &__empty {
    padding: 32px 16px;
    text-align: center;
    font-size: 13px;
    color: #909399;
    border: 1px dashed #e8eaec;
    border-radius: 8px;
    background: #fafafa;
    line-height: 1.6;
  }

  &__empty-link {
    padding: 0 4px !important;
    color: #5668f0 !important;
    font-size: 13px !important;
    height: auto !important;
    vertical-align: baseline;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid #e8eaec;
    border-radius: 8px;
    background: #fff;
  }

  &__item-main {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  &__item-icon {
    flex-shrink: 0;
    color: #a5a5a5;
  }

  &__item-name {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__item-tag {
    flex-shrink: 0;
    margin: 0 !important;
    border: none !important;
    background: #f4f4f5 !important;
    color: #909399 !important;
    font-size: 12px !important;
    line-height: 20px !important;
    height: 22px !important;
    padding: 0 8px !important;
  }

  &__item-actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }
}
</style>
